"use client";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Plus } from "lucide-react";
import { useState } from "react";
import type { AccessibilityStatement } from "../../types/statement";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";
import { z } from "zod";
import { HelpText } from "../help-text";

export const conformanceStatusSchema = z.object({
  standard: z
    .string()
    .min(1, { message: "Accessibility standard is required" }),
  conformanceLevel: z
    .string()
    .min(1, { message: "Conformance status is required" }),
  customStandard: z.string().optional(),
  additionalConsiderations: z.string().optional(),
  knownIssues: z.any(),
  unresolvedBarriers: z.string().optional(),
  contentOutsideScope: z.string().optional(),
});

interface ConformanceStatusStepProps {
  data: AccessibilityStatement["conformanceStatus"];
  onChange: (
    data: AccessibilityStatement["conformanceStatus"],
    changedField?: keyof AccessibilityStatement["conformanceStatus"]
  ) => void;
  externalErrors?: { standard?: string; conformanceLevel?: string } | null;
}

const standardOptions = [
  { value: "wcag22-aa", label: "WCAG 2.2 Level AA" },
  { value: "wcag21-aa", label: "WCAG 2.1 level AA" },
  { value: "wcag20-aa", label: "WCAG 2.0 level AA" },
  { value: "other", label: "Other (Please specify)" },
];

const conformanceLevels = [
  {
    value: "fully-conformant",
    label: "Fully conformant:",
    description:
      "The content fully conforms to the accessibility standard without any exceptions",
  },
  {
    value: "partially-conformant",
    label: "Partially conformant:",
    description:
      "Some parts of the content do not fully conform to the accessibility standard (you can indicate these parts in later sections of this form)",
  },
  {
    value: "non-conformant",
    label: "Non conformant:",
    description:
      "The content fully conforms to the accessibility standard without any exceptions",
  },
  {
    value: "not-assessed",
    label: "Not assessed",
    description:
      "The content has not been evaluated or the evaluation results are not available",
  }
];

export function ConformanceStatusStep({
  data,
  onChange,
  externalErrors,
}: ConformanceStatusStepProps) {
  const [issuesOpen, setIssuesOpen] = useState(true);
  const [barriersOpen, setBarriersOpen] = useState(false);
  const [contentOutsideScopeOpen, setContentOutsideScopeOpen] = useState(false);
  const [errors, setErrors] = useState<{
    standard?: string;
    conformanceLevel?: string;
  }>({});

  const handleStandardChange = (value: string) => {
    // Validate
    const fieldSchema = z.object({
      standard: conformanceStatusSchema.shape.standard,
    });
    const result = fieldSchema.safeParse({ standard: value });
    if (!result.success) {
      setErrors((prev) => ({
        ...prev,
        standard: result.error.errors[0]?.message || "",
      }));
    } else {
      setErrors((prev) => ({ ...prev, standard: undefined }));
    }
    onChange({ ...data, standard: value }, "standard");
  };

  const handleConformanceLevelChange = (value: string) => {
    // Validate
    const fieldSchema = z.object({
      conformanceLevel: conformanceStatusSchema.shape.conformanceLevel,
    });
    const result = fieldSchema.safeParse({ conformanceLevel: value });
    if (!result.success) {
      setErrors((prev) => ({
        ...prev,
        conformanceLevel: result.error.errors[0]?.message || "",
      }));
    } else {
      setErrors((prev) => ({ ...prev, conformanceLevel: undefined }));
    }
    onChange({ ...data, conformanceLevel: value }, "conformanceLevel");
  };

  const addKnownIssue = () => {
    if (data.knownIssues.length < 5) {
      onChange(
        {
          ...data,
          knownIssues: [...data.knownIssues, { title: "", description: "" }],
        },
        "knownIssues"
      );
    }
  };

  const updateKnownIssue = (
    index: number,
    field: "title" | "description",
    value: string
  ) => {
    const updatedIssues = [...data.knownIssues];
    updatedIssues[index][field] = value;
    onChange({ ...data, knownIssues: updatedIssues }, "knownIssues");
  };

  // Prefer externalErrors if present, otherwise use local errors
  const displayError = (field: keyof typeof errors) => {
    return externalErrors?.[field] || errors[field];
  };

  return (
    <div className="cy:space-y-6">
      <div>
        <h2 className="cy:text-[28px]! cy:font-semibold cy:mb-2">
          Conformance status
        </h2>
      </div>

      <div className="cy:space-y-6">
        <div>
          <span
            id="standard"
            style={{
              position: "absolute",
              left: "-9999px",
              height: 0,
              width: 0,
            }}
            aria-hidden="true"
          />
          <Label className="cy:text-lg cy:font-semibold cy:flex! cy:items-center cy:gap-1 cy:mb-4!">
            Which accessibility standard have you been following?*
            <TooltipWrapper content="Select the accessibility standard your organisation follows. The Web Content Accessibility Guidelines (WCAG) are globally recognised, and the current version is 2.2." />
          </Label>
          <RadioGroup
            value={data.standard}
            onValueChange={handleStandardChange}
            className="cy:rounded-full!"
          >
            {standardOptions.map((option) => (
              <>
                <div
                  key={option.value}
                  className="cy:flex cy:items-start cy:space-x-2"
                >
                  <RadioGroupItem
                    value={option.value}
                    id={option.value}
                    className="cy:mt-1! cy:mr-3! cy:rounded-full!"
                  />
                  <Label
                    htmlFor={option.value}
                    className="cy:text-base cy:mb-0! cy:block"
                  >
                    {option.label}
                  </Label>
                </div>
              </>
            ))}
          </RadioGroup>
          {displayError("standard") && (
            <div className="cy:text-red-600 cy:text-sm cy:mt-1">
              {displayError("standard")}
            </div>
          )}
          {data.standard === "other" && (
            <>
              <HelpText>Example: WCAG 2.1 Level AAA</HelpText>
              <Input
                value={data.customStandard}
                onChange={(e) =>
                  onChange({ ...data, customStandard: e.target.value })
                }
                className="cy:mt-2"
              />
            </>
          )}
        </div>

        <div>
          <span
            id="conformanceLevel"
            style={{
              position: "absolute",
              left: "-9999px",
              height: 0,
              width: 0,
            }}
            aria-hidden="true"
          />
          <Label className="cy:text-lg cy:font-semibold cy:flex! cy:items-center cy:gap-1 cy:mb-4!">
            Current content conformance status?*
            <TooltipWrapper content="How closely does your website follow the accessibility standard mentioned above? Full conformance isn't always possible, and you can explain any exceptions or support options later in the form." />
          </Label>
          <RadioGroup
            value={data.conformanceLevel}
            onValueChange={handleConformanceLevelChange}
          >
            {conformanceLevels.map((level) => (
              <div key={level.value} className="cy:space-y-1">
                <div className="cy:flex cy:items-start cy:space-x-2">
                  <RadioGroupItem
                    value={level.value}
                    id={level.value}
                    className="cy:mt-1! cy:mr-3! cy:rounded-full!"
                  />
                  <Label
                    htmlFor={level.value}
                    className="cy:text-base cy:font-semibold cy:mb-0!"
                  >
                    {level.label}
                  </Label>
                </div>
                {level.description && (
                  <p className="cy:text-base cy:font-normal cy:ml-6 ">
                    {level.description}
                  </p>
                )}
              </div>
            ))}
          </RadioGroup>
          {displayError("conformanceLevel") && (
            <div className="cy:text-red-600 cy:text-sm cy:mt-1">
              {displayError("conformanceLevel")}
            </div>
          )}
        </div>

        <div className="cy:space-y-2">
          <Label
            htmlFor="additional-considerations"
            className="cy:text-lg cy:font-semibold cy:flex! cy:gap-1"
          >
            Additional accessibility considerations
            <TooltipWrapper content="Sometimes, additional accessibility features beyond the stated standard may be applied. For example, sign language videos or context-sensitive help. You can list these extra enhancements here." />
          </Label>
          <HelpText>
            Example: While aiming for WCAG 2.2 Level AA conformance, we also
            implemented some Level AAA criteria, such as decorative-only images
            of text, data preservation after session expiration, and sign
            language interpretation in some videos.
          </HelpText>
          <Textarea
            id="additional-considerations"
            value={data.additionalConsiderations}
            onChange={(e) =>
              onChange({ ...data, additionalConsiderations: e.target.value })
            }
            className="cy:mt-2 cy:h-[140px] cy:rounded cy:text-base!"
            rows={8}
          />
        </div>

        <div>
          <Label className="cy:text-lg cy:font-semibold cy:mb-4! cy:flex! cy:gap-1">
            Accessibility limitations
            <TooltipWrapper content="List content that has accessibility limitations, explain the issue users may encounter, and why it occurs. If possible, suggest accessible alternatives or provide contact information for assistance.This helps maintain transparency and aligns with requirements such as the EU Web Accessibility Directive." />
          </Label>

          <Collapsible
            open={issuesOpen}
            onOpenChange={setIssuesOpen}
            className="cy:bg-white cy:px-7.5 cy:py-1"
          >
            <CollapsibleTrigger className="cy:flex cy:items-center cy:justify-between cy:w-full cy:text-left cy:border-b cy:pb-3">
              <span className="cy:text-base cy:font-medium cy:grid cy:grid-cols-20 cy:gap-1">
                <span className="cy:text-base cy:font-medium">1. </span>
                <span className="cy:text-base cy:font-semibold cy:col-span-19">
                  Features/content that do not meet accessibility standards
                </span>
              </span>
              <ChevronDown
                className={`w-4 cy:h-4 cy:transition-transform ${
                  issuesOpen ? "cy:cy:rotate-180" : ""
                }`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="cy:space-y-4 cy:mt-4">
              <div className="cy:grid cy:grid-cols-20 cy:gap-1">
                <div className="cy:col-span-1"></div>
                <div className="cy:col-span-19 cy:space-y-4">
                  {data.knownIssues.map((issue, index) => (
                    <div key={index} className="cy:space-y-2">
                      <div className="cy:flex cy:flex-col cy:gap-1">
                        <Label
                          htmlFor={`issue-title-${index}`}
                          className="cy:text-base cy:font-semibold"
                        >
                          Title
                        </Label>
                        <HelpText>Example: Videos</HelpText>
                        <Input
                          id={`issue-title-${index}`}
                          value={issue.title}
                          onChange={(e) =>
                            updateKnownIssue(index, "title", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor={`issue-desc-${index}`}
                          className="cy:text-base cy:font-semibold"
                        >
                          Issue description and expected resolution timeline
                        </Label>
                        <HelpText>
                          Example: Our videos currently lack closed captions,
                          and we plan to add accurate captions to all existing
                          and new videos by the end of Q3 2025.
                        </HelpText>
                        <Textarea
                          id={`issue-desc-${index}`}
                          value={issue.description}
                          onChange={(e) =>
                            updateKnownIssue(
                              index,
                              "description",
                              e.target.value
                            )
                          }
                          className="cy:mt-2 cy:h-[118px] cy:rounded cy:text-base!"
                        />
                      </div>
                    </div>
                  ))}
                  {data.knownIssues.length < 5 && (
                    <button
                      type="button"
                      onClick={addKnownIssue}
                      className="cy:flex cy:items-center cy:gap-3 cy:bg-transparent cy:border-none cy:cursor-pointer cy:p-0 cy:text-base cy:font-normal cy:text-slate-800 cy:focus:outline-none cy:mt-3"
                    >
                      <span className="cy:flex cy:items-center cy:justify-center cy:w-5 cy:h-5 cy:rounded-full cy:bg-primary cy:text-white cy:text-base">
                        <Plus className="cy:w-3 cy:h-3" />
                      </span>
                      Add another issue
                    </button>
                  )}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Collapsible
            open={barriersOpen}
            onOpenChange={setBarriersOpen}
            className="cy:mt-4 cy:bg-white cy:px-7.5 cy:py-1"
          >
            <CollapsibleTrigger className="cy:flex cy:items-center cy:justify-between cy:w-full cy:text-left cy:border-b cy:pb-3">
              <span className="cy:text-base cy:font-medium cy:grid cy:grid-cols-20 cy:gap-1">
                <span className="cy:text-base cy:font-medium">2. </span>
                <span className="cy:text-base cy:font-semibold cy:col-span-19 cy:space-x-3">
                  Unresolved accessibility barriers due to budget or technical
                  limitations (Disproportionate burden)
                  <TooltipWrapper
                    className="cy:ml-1"
                    content="Explain which accessibility issues cannot be resolved at this time, why (budget or technical limitations), and when you will review."
                  />
                </span>
              </span>
              <ChevronDown
                className={`w-4 cy:h-4 cy:transition-transform ${
                  barriersOpen ? "cy:cy:rotate-180" : ""
                }`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="cy:space-y-4 cy:mt-4">
              <div className="cy:grid cy:grid-cols-20 cy:gap-1">
                <div className="cy:col-span-1"></div>
                <div className="cy:col-span-19 cy:space-y-2">
                  <Label
                    htmlFor="unresolved-barriers"
                    className="cy:text-base cy:font-semibold"
                  >
                    Describe the issue
                  </Label>
                  <HelpText>
                    Example: Providing full video captions for our archived
                    webinars is not currently possible due to budget
                    limitations. We will review this annually.
                  </HelpText>
                  <Textarea
                    id="unresolved-barriers"
                    value={data.unresolvedBarriers}
                    onChange={(e) =>
                      onChange({
                        ...data,
                        unresolvedBarriers: e.target.value,
                      })
                    }
                    className="cy:mt-2 cy:h-[118px] cy:rounded cy:text-base!"
                    rows={8}
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Content Outside the Scope of Accessibility Legislation */}
          <Collapsible
            open={contentOutsideScopeOpen}
            onOpenChange={setContentOutsideScopeOpen}
            className="cy:mt-4 cy:bg-white cy:px-7.5 cy:py-1"
          >
            <CollapsibleTrigger className="cy:flex cy:items-center cy:justify-between cy:w-full cy:text-left cy:mb-2 cy:border-b cy:pb-3">
              <span className="cy:text-base cy:font-medium cy:grid cy:grid-cols-20 cy:gap-1">
                <span className="cy:text-base cy:font-medium">3. </span>
                <span className="cy:text-base cy:font-semibold cy:col-span-19 cy:space-x-3">
                  Content outside the scope of accessibility legislation
                  <TooltipWrapper
                    className="cy:ml-1"
                    content="Describe any content that is not covered by accessibility laws, such as third-party content you do not control."
                  />
                </span>
              </span>
              <ChevronDown
                className={`w-4 cy:h-4 cy:transition-transform ${
                  barriersOpen ? "cy:cy:rotate-180" : ""
                }`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="cy:space-y-4 cy:mt-0">
              <div className="cy:grid cy:grid-cols-20 cy:gap-1">
                <div className="cy:col-span-1"></div>
                <div className="cy:col-span-19 cy:space-y-2">
                  <Label
                    htmlFor="content-outside-scope"
                    className="cy:text-base cy:font-semibold"
                  >
                    Describe the issue
                  </Label>
                  <HelpText>
                    Example: Live video streams without captions are currently
                    exempt under the regulations.
                  </HelpText>
                  <Textarea
                    id="content-outside-scope"
                    value={data.contentOutsideScope}
                    onChange={(e) =>
                      onChange({ ...data, contentOutsideScope: e.target.value })
                    }
                    className="cy:mt-2 cy:h-[118px] cy:rounded cy:text-base!"
                    rows={8}
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    </div>
  );
}
